import React, { useEffect, useMemo, useState } from 'react';
import supabase from '../config/supabase';

const toEmailList = (emails) => {
  if (!Array.isArray(emails)) return ['', '', ''];
  return [emails[0] || '', emails[1] || '', emails[2] || ''];
};

const normalizeEmail = (value) => String(value || '').trim();

const OrderApprovalSettings = () => {
  const [loading, setLoading] = useState(true);
  const [savingRowId, setSavingRowId] = useState(null);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [storeId, setStoreId] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    void loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError('');
    setFeedback('');
    try {
      const selectedStoreId = Number(localStorage.getItem('selectedStoreId'));
      if (!selectedStoreId) {
        throw new Error('Select a store before managing order approval settings.');
      }
      setStoreId(selectedStoreId);

      const { data: currentUserData } = await supabase.auth.getUser();
      const currentUser = currentUserData?.user;
      if (!currentUser) throw new Error('You must be logged in.');

      const [{ data: membership }, { data: storeData }] = await Promise.all([
        supabase
          .from('store_users')
          .select('id, role, can_manage_users')
          .eq('store_id', selectedStoreId)
          .eq('user_id', currentUser.id)
          .eq('is_active', true)
          .maybeSingle(),
        supabase
          .from('stores')
          .select('id, owner_id')
          .eq('id', selectedStoreId)
          .maybeSingle(),
      ]);
      const canManageUsers = Boolean(
        (storeData?.owner_id && storeData.owner_id === currentUser.id)
        || membership?.role === 'owner'
        || membership?.can_manage_users === true
      );
      setCanManage(canManageUsers);

      const { data: storeUsers, error: storeUsersError } = await supabase
        .from('store_users')
        .select('id, user_id, role, is_active')
        .eq('store_id', selectedStoreId)
        .eq('is_active', true)
        .order('role', { ascending: true });
      if (storeUsersError) throw storeUsersError;

      const userIds = [...new Set((storeUsers || []).map((row) => row.user_id).filter(Boolean))];
      const [{ data: profiles, error: profilesError }, { data: orderSettings, error: settingsError }] = await Promise.all([
        userIds.length
          ? supabase.from('user_profiles').select('id, email, first_name, last_name').in('id', userIds)
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('store_user_order_settings')
          .select('id, store_user_id, user_id, send_orders, require_order_approval, approval_emails')
          .eq('store_id', selectedStoreId),
      ]);
      if (profilesError) throw profilesError;
      if (settingsError) throw settingsError;

      const profileByUser = new Map((profiles || []).map((profile) => [profile.id, profile]));
      const settingsByStoreUserId = new Map((orderSettings || []).map((row) => [row.store_user_id, row]));

      const rows = (storeUsers || []).map((storeUser) => {
        const profile = profileByUser.get(storeUser.user_id) || {};
        const existing = settingsByStoreUserId.get(storeUser.id) || {};
        return {
          storeUserId: storeUser.id,
          userId: storeUser.user_id,
          role: storeUser.role || 'member',
          fullName: [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim() || profile.email || 'Unnamed user',
          email: profile.email || '',
          settingsRowId: existing.id || null,
          sendOrders: existing.send_orders ?? true,
          requireApproval: existing.require_order_approval ?? false,
          approvalEmails: toEmailList(existing.approval_emails),
        };
      });
      setMembers(rows);
    } catch (loadError) {
      setError(loadError?.message || 'Unable to load order settings.');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const setMemberField = (storeUserId, updater) => {
    setMembers((previous) => previous.map((member) => (
      member.storeUserId === storeUserId
        ? { ...member, ...updater(member) }
        : member
    )));
  };

  const handleSaveRow = async (row) => {
    if (!storeId) return;
    setSavingRowId(row.storeUserId);
    setError('');
    setFeedback('');
    try {
      const cleanEmails = row.approvalEmails
        .map(normalizeEmail)
        .filter(Boolean)
        .slice(0, 3);

      const { error: upsertError } = await supabase
        .from('store_user_order_settings')
        .upsert({
          store_user_id: row.storeUserId,
          store_id: storeId,
          user_id: row.userId,
          send_orders: row.sendOrders,
          require_order_approval: row.requireApproval,
          approval_emails: cleanEmails,
        }, { onConflict: 'store_user_id' });
      if (upsertError) throw upsertError;
      setFeedback(`Saved order settings for ${row.fullName}.`);
      setMemberField(row.storeUserId, () => ({ approvalEmails: toEmailList(cleanEmails) }));
    } catch (saveError) {
      setError(saveError?.message || 'Unable to save order settings.');
    } finally {
      setSavingRowId(null);
    }
  };

  const activeMembers = useMemo(() => members.filter((member) => member.email), [members]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-4 text-muted">Loading order approval settings...</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Order Send & Approval Settings</h5>
      </div>
      <div className="card-body">
        <p className="text-muted">
          Store owners/admins can control who sends vendor purchase orders and whether approvals are required.
          When approval is required, the requester&apos;s email is included by default and up to 2 additional approver emails can be added.
        </p>
        {!canManage ? (
          <div className="alert alert-info">You can view these settings but cannot edit them for this store.</div>
        ) : null}
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        {feedback ? <div className="alert alert-success py-2">{feedback}</div> : null}

        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Send Orders</th>
                <th>Require Approval</th>
                <th>Approval Emails (up to 3)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeMembers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-3">No active store users found.</td>
                </tr>
              ) : activeMembers.map((member) => (
                <tr key={member.storeUserId}>
                  <td>
                    <div><strong>{member.fullName}</strong></div>
                    <div className="small text-muted">{member.email}</div>
                  </td>
                  <td>{member.role}</td>
                  <td>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={member.sendOrders}
                        disabled={!canManage}
                        onChange={(event) => setMemberField(member.storeUserId, () => ({ sendOrders: event.target.checked }))}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={member.requireApproval}
                        disabled={!canManage || !member.sendOrders}
                        onChange={(event) => setMemberField(member.storeUserId, () => ({ requireApproval: event.target.checked }))}
                      />
                    </div>
                  </td>
                  <td style={{ minWidth: 260 }}>
                    {member.approvalEmails.map((email, index) => (
                      <input
                        key={`${member.storeUserId}-approval-${index}`}
                        type="email"
                        className="form-control form-control-sm mb-1"
                        value={email}
                        disabled={!canManage || !member.requireApproval}
                        onChange={(event) => setMemberField(member.storeUserId, (current) => {
                          const nextEmails = [...current.approvalEmails];
                          nextEmails[index] = event.target.value;
                          return { approvalEmails: nextEmails };
                        })}
                        placeholder={index === 0 ? 'Approver email (requester email auto-included)' : 'Additional approver email'}
                      />
                    ))}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!canManage || savingRowId === member.storeUserId}
                      onClick={() => void handleSaveRow(member)}
                    >
                      {savingRowId === member.storeUserId ? 'Saving...' : 'Save'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderApprovalSettings;

